import $ from 'jquery'

const activeClass = 'is-active'
const initializedClass = 'dhsv_vc_hero_media_slider--is-initialized'
const pausedClass = 'dhsv_vc_hero_media_slider--is-paused'
const mobileQuery = window.matchMedia('(max-width: 767px)')
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
const swipeThreshold = 50

function loadVideo(video) {
  if (!video || video.dataset.loaded === 'true') {
    return
  }

  const useMobile = mobileQuery.matches && video.dataset.srcMobile
  const source = document.createElement('source')
  source.src = useMobile ? video.dataset.srcMobile : video.dataset.src
  source.type = (useMobile ? video.dataset.typeMobile : video.dataset.type) || 'video/mp4'
  video.appendChild(source)
  video.dataset.loaded = 'true'
  video.load()
}

function initHeroMediaSlider(container) {
  if (container.dataset.initialized === 'true') {
    return
  }
  container.dataset.initialized = 'true'

  const slides = Array.from(
    container.querySelectorAll('.dhsv_vc_hero_media_slider__slide')
  )
  const dots = Array.from(
    container.querySelectorAll('.js-dhsv-hero-media-slider-dot')
  )
  const prevButton = container.querySelector('.js-dhsv-hero-media-slider-prev')
  const nextButton = container.querySelector('.js-dhsv-hero-media-slider-next')
  const pauseButton = container.querySelector('.js-dhsv-hero-media-slider-pause')
  const intervalMs = parseFloat(container.dataset.interval || '0') * 1000
  const isSingle = slides.length <= 1
  const advanceOnVideoEnd =
    !isSingle && intervalMs > 0 && container.dataset.videoAdvance === 'ended'

  let currentIndex = 0
  let elapsed = 0
  let lastTick = null
    let inView = true
  let userPaused = reducedMotionQuery.matches
  // true when the active video could not autoplay (e.g. power saving mode)
  let videoBlocked = false

  const getVideo = index =>
    slides[index] &&
    slides[index].querySelector('.js-dhsv-hero-media-slider-video')

  slides.forEach(slide => {
    const video = slide.querySelector('.js-dhsv-hero-media-slider-video')
    if (!video) {
      return
    }
    // a slide with a video loops it unless the slider moves on when it ends
    video.loop = !advanceOnVideoEnd
    video.addEventListener('ended', () => {
      if (advanceOnVideoEnd && video === getVideo(currentIndex)) {
        goTo(currentIndex + 1)
      }
    })
  })

  function isRunning() {
    return !userPaused && inView && !document.hidden
  }

  function setProgress(value) {
    const dot = dots[currentIndex]
    if (dot) {
      dot.style.setProperty('--progress', Math.min(Math.max(value, 0), 1))
    }
  }

  function tick(now) {
    window.requestAnimationFrame(tick)

    if (!isRunning() || isSingle || intervalMs <= 0) {
      lastTick = null
      return
    }

    const video = getVideo(currentIndex)

    if (video && advanceOnVideoEnd && !videoBlocked) {
      // progress follows the video, the "ended" event moves on
      if (video.duration) {
        setProgress(video.currentTime / video.duration)
      }
      lastTick = null
      return
    }

    if (lastTick !== null) {
      elapsed += now - lastTick
    }
    lastTick = now
    setProgress(elapsed / intervalMs)

    if (elapsed >= intervalMs) {
      goTo(currentIndex + 1)
    }
  }

  function playActiveVideo() {
    const video = getVideo(currentIndex)
    if (!video) {
      return
    }

    loadVideo(video)

    if (!isRunning()) {
      video.pause()
      return
    }

    const promise = video.play()
    if (promise && promise.catch) {
      promise
        .then(() => {
          videoBlocked = false
        })
        .catch(() => {
          // poster stays visible, slide duration applies instead
          videoBlocked = true
        })
    }
  }

  function goTo(index) {
    if (isSingle) {
      return
    }

    const nextIndex = (index + slides.length) % slides.length
    const previousVideo = getVideo(currentIndex)

    if (previousVideo) {
      previousVideo.pause()
      try {
        previousVideo.currentTime = 0
      } catch (e) {
        // ignore, metadata not loaded yet
      }
    }

    setProgress(0)
    currentIndex = nextIndex
    elapsed = 0
    lastTick = null
    videoBlocked = false
    update()
    playActiveVideo()

    // warm up the following video so it starts without delay
    const upcomingVideo = getVideo((currentIndex + 1) % slides.length)
    if (upcomingVideo) {
      upcomingVideo.preload = 'metadata'
      loadVideo(upcomingVideo)
    }
  }

  function update() {
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === currentIndex
      slide.classList.toggle(activeClass, isActive)
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true')
      slide.inert = !isActive
      slide.querySelectorAll('a, button').forEach(el => {
        el.tabIndex = isActive ? 0 : -1
      })
    })
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === currentIndex
      dot.classList.toggle(activeClass, isActive)
      if (isActive) {
        dot.setAttribute('aria-current', 'true')
      } else {
        dot.removeAttribute('aria-current')
        dot.style.setProperty('--progress', 0)
      }
    })
  }

  function updatePauseButton() {
    container.classList.toggle(pausedClass, userPaused)
    if (pauseButton) {
      pauseButton.setAttribute(
        'aria-label',
        userPaused ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause
      )
    }
  }

  function onRunningStateChange() {
    lastTick = null
    const video = getVideo(currentIndex)
    if (!video) {
      return
    }
    if (isRunning()) {
      playActiveVideo()
    } else {
      video.pause()
    }
  }

  if (prevButton) {
    prevButton.addEventListener('click', () => goTo(currentIndex - 1))
  }
  if (nextButton) {
    nextButton.addEventListener('click', () => goTo(currentIndex + 1))
  }
  dots.forEach((dot, dotIndex) => {
    dot.addEventListener('click', () => {
      if (dotIndex !== currentIndex) {
        goTo(dotIndex)
      }
    })
  })
  if (pauseButton) {
    pauseButton.addEventListener('click', () => {
      userPaused = !userPaused
      updatePauseButton()
      onRunningStateChange()
    })
  }

  container.addEventListener('keydown', event => {
    if (event.key === 'ArrowLeft') {
      goTo(currentIndex - 1)
    } else if (event.key === 'ArrowRight') {
      goTo(currentIndex + 1)
    }
  })

  // swipe on touch devices
  let touchStartX = null
  let touchStartY = null
  container.addEventListener(
    'touchstart',
    event => {
      touchStartX = event.touches[0].clientX
      touchStartY = event.touches[0].clientY
    },
    { passive: true }
  )
  container.addEventListener(
    'touchend',
    event => {
      if (touchStartX === null) {
        return
      }
      const deltaX = event.changedTouches[0].clientX - touchStartX
      const deltaY = event.changedTouches[0].clientY - touchStartY
      touchStartX = null
      if (
        Math.abs(deltaX) > swipeThreshold &&
        Math.abs(deltaX) > Math.abs(deltaY)
      ) {
        goTo(currentIndex + (deltaX < 0 ? 1 : -1))
      }
    },
    { passive: true }
  )

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      entries => {
        inView = entries[0].isIntersecting
        onRunningStateChange()
      },
      { threshold: 0.25 }
    ).observe(container)
  }

  document.addEventListener('visibilitychange', onRunningStateChange)

  // fullscreen hero fills the screen below the fixed header
  function updateOffset() {
    const top = container.getBoundingClientRect().top + window.pageYOffset
    container.style.setProperty(
      '--hero-offset',
      top < window.innerHeight / 2 ? `${Math.round(top)}px` : '0px'
    )
  }
  if (container.classList.contains('dhsv_vc_hero_media_slider--fullscreen')) {
    updateOffset()
    window.addEventListener('resize', updateOffset)
    window.addEventListener('load', updateOffset)
  }

  update()
  updatePauseButton()
  playActiveVideo()
  container.classList.add(initializedClass)

  if (!isSingle) {
    window.requestAnimationFrame(tick)
  }
}

function initAll() {
  document
    .querySelectorAll('.js-dhsv-hero-media-slider')
    .forEach(initHeroMediaSlider)
}

$(document).ready(initAll)
// frontend editor re-renders elements
$(document).on('vc_reload', initAll)
